import {
  type APIConfiguration,
  Service,
  type SWRConfiguration,
  useGet,
  useList,
  useListWithCount,
  useRead,
} from "@api/common";
import { chunk } from "lodash";
import type {
  BulkCreateParams,
  BulkCreateResult,
  BulkCreateRowResult,
  Customer,
  ListMember,
  ListParams,
  ReadParams,
} from "./types";
import { getParamsWithPagination } from "./utils";

export type { Customer };
export interface CustomerProperties {
  properties: Record<string, CustomerProperty>;
}

export interface CustomerProperty {
  type: "string" | "number" | "currency" | "boolean" | "date" | "enum";
}

export class CustomerService extends Service<Customer> {
  constructor() {
    super("/customers");
  }

  async bulkCreate(
    body: BulkCreateParams,
    callback?: (progress: number) => void,
  ): Promise<BulkCreateResult> {
    const total = body.customers.length;
    const customers = new Set<string>();
    const results: BulkCreateRowResult[] = [];
    let count = 0;
    for (const _customers of chunk(body.customers, 100)) {
      const offset = count;
      const response = await this.post<BulkCreateResult>(`${this.basePath}/bulk`, {
        gym: body.gym,
        customers: _customers,
      });
      for (const id of response.customers ?? []) {
        customers.add(id);
      }
      for (const result of response.results ?? []) {
        results.push({ ...result, index: result.index + offset });
      }
      count += _customers.length;
      callback?.(Math.round((count / total) * 100));
    }
    return { customers: Array.from(customers), results };
  }
  async listCustomerProperties(params: { gym: string; sample: number }): Promise<any> {
    const response = await this.get(`${this.basePath}/available-properties`, params);
    return response;
  }
  async listPropertyValues(property: string, params: ListParams<any>): Promise<any> {
    const response = await this.get(`${this.basePath}/available-property-values`, {
      property,
      ...params,
    });
    return response;
  }
  async listCustomerEvents(params: { gym: string; sample: number }): Promise<any> {
    const response = await this.get(`${this.basePath}/available-events`, params);
    return response;
  }
  async listEventValues(property: string, params: ListParams<any>): Promise<any> {
    const response = await this.get(`${this.basePath}/available-event-values`, {
      property,
      ...params,
    });
    return response;
  }
}

export const customerService = new CustomerService();

export function useCustomers(params?: ListParams<Customer>, config?: APIConfiguration) {
  return useList(customerService, { ...getParamsWithPagination(params) }, config);
}
export function useCustomersWithCount(params?: ListParams<Customer>, config?: SWRConfiguration) {
  return useListWithCount(customerService, { ...getParamsWithPagination(params) }, config);
}

export function useCustomersProperties(
  params?: { gym: string; sample: number },
  config?: SWRConfiguration,
) {
  return useGet<CustomerProperties>(
    customerService,
    `${customerService.basePath}/available-properties`,
    params,
    config,
  );
}

export function useCustomer(id?: string, params?: ReadParams, config?: SWRConfiguration) {
  return useRead(customerService, id, params, config);
}

export function useCustomerListMembers(
  id?: string,
  params?: ReadParams,
  config?: SWRConfiguration,
) {
  return useGet<ListMember[]>(
    customerService,
    `${customerService.basePath}/${id}/list-members`,
    params,
    config,
  );
}
